import React, { useMemo, useState } from 'react';

const SPORT_COLORS = {
    'nba': '#f97316',
    'afl': '#3b82f6',
    'nfl': '#22c55e',
    'nhl': '#8b5cf6',
    'mlb': '#e11d48',
    'soccer': '#06b6d4',
    'tennis': '#eab308',
    'cricket': '#ec4899',
};

function SportBreakdown({ bets, displayMode = 'dollars', unitSize = 25, getUnitSizeForDate, advancedUnitSettings = false }) {
    const [sortBy, setSortBy] = useState('pl');
    const isUnits = displayMode === 'units';

    const sportData = useMemo(() => {
        const grouped = {};

        bets.forEach(bet => {
            const sport = (bet.sport || 'unknown').toLowerCase();
            const divisor = isUnits && advancedUnitSettings && getUnitSizeForDate ? getUnitSizeForDate(bet.date) : (isUnits ? unitSize : 1);

            if (!grouped[sport]) {
                grouped[sport] = {
                    name: sport,
                    bets: 0,
                    wins: 0,
                    losses: 0,
                    totalStaked: 0,
                    totalReturned: 0,
                    pl: 0
                };
            }

            grouped[sport].bets++;
            if (!bet.bonus_bet) {
                grouped[sport].totalStaked += bet.stake / divisor;
            }
            grouped[sport].pl += bet.pl / divisor;

            if (bet.result !== 'pending') {
                grouped[sport].totalReturned += ((bet.bonus_bet ? 0 : bet.stake) + bet.pl) / divisor;
            }

            if (bet.result === 'win') grouped[sport].wins++;
            if (bet.result === 'loss') grouped[sport].losses++;
        });

        return Object.values(grouped)
            .map(s => ({
                ...s,
                winRate: (s.wins + s.losses) > 0 ? (s.wins / (s.wins + s.losses)) * 100 : 0,
                roi: s.totalStaked > 0 ? (s.pl / s.totalStaked) * 100 : 0
            }))
            .sort((a, b) => {
                if (sortBy === 'pl') return b.pl - a.pl;
                if (sortBy === 'roi') return b.roi - a.roi;
                return 0;
            });
    }, [bets, sortBy, isUnits, unitSize, advancedUnitSettings, getUnitSizeForDate]);

    const chartData = useMemo(() => {
        return [...sportData].sort((a, b) => b.pl - a.pl);
    }, [sportData]);

    const maxAbsPL = Math.max(...sportData.map(s => Math.abs(s.pl)), 1);
    const totalPL = sportData.reduce((sum, s) => sum + s.pl, 0);

    const formatCurrency = (value) => {
        const prefix = value >= 0 ? '+' : '';
        if (isUnits) return `${prefix}${value.toFixed(1)}U`;
        return `${prefix}$${Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    };

    const formatCurrencyFull = (value) => {
        const prefix = value >= 0 ? '+' : '';
        if (isUnits) return `${prefix}${value.toFixed(1)}U`;
        return `${prefix}$${Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatStaked = (value) => {
        if (isUnits) return `${value.toFixed(1)}U`;
        return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const getSportColor = (name) => SPORT_COLORS[name] || '#64748b';

    if (sportData.length <= 1) return null;

    return (
        <div className="bg-slate-900/50 rounded-2xl border border-slate-700/50 p-4 md:p-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                <div className="flex items-center justify-between sm:block">
                    <div>
                        <h3 className="text-base md:text-lg font-semibold text-white mb-0.5 md:mb-1">Performance by Sport</h3>
                        <p className="text-xs md:text-sm text-slate-400 hidden sm:block">Results breakdown by sport</p>
                    </div>
                    <div className={`text-lg md:text-xl font-bold sm:hidden ${totalPL >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {formatCurrencyFull(totalPL)}
                    </div>
                </div>
                <div className={`hidden sm:block text-lg md:text-xl font-bold ${totalPL >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {formatCurrencyFull(totalPL)}
                </div>
            </div>

            {/* P/L by Sport - Horizontal Bar Chart */}
            <div className="mb-6">
                <div className="text-xs text-slate-500 uppercase tracking-wider mb-3">P/L by Sport</div>
                <div className="space-y-2">
                    {chartData.map((sport) => {
                        const barWidth = (Math.abs(sport.pl) / maxAbsPL) * 50;
                        const isPositive = sport.pl >= 0;

                        return (
                            <div key={sport.name} className="flex items-center gap-2 group" title={sport.name.toUpperCase()}>
                                <div className="w-8 flex items-center justify-center">
                                    <div
                                        className="w-6 h-6 rounded flex items-center justify-center text-[10px] font-bold text-white"
                                        style={{ backgroundColor: getSportColor(sport.name) }}
                                    >
                                        {sport.name.substring(0, 3).toUpperCase()}
                                    </div>
                                </div>

                                <div className="flex-1 flex items-center">
                                    <div className="w-1/2 flex justify-end">
                                        {!isPositive && (
                                            <div
                                                className="h-5 rounded-l-sm bg-gradient-to-l from-red-500 to-red-600 transition-all duration-300 group-hover:from-red-400 group-hover:to-red-500"
                                                style={{
                                                    width: `${barWidth}%`,
                                                    boxShadow: '0 0 10px rgba(239, 68, 68, 0.3)'
                                                }}
                                            />
                                        )}
                                    </div>
                                    <div className="w-px h-6 bg-slate-600" />
                                    <div className="w-1/2 flex justify-start">
                                        {isPositive && (
                                            <div
                                                className="h-5 rounded-r-sm bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-300 group-hover:from-emerald-400 group-hover:to-teal-400"
                                                style={{
                                                    width: `${barWidth}%`,
                                                    boxShadow: '0 0 10px rgba(16, 185, 129, 0.3)'
                                                }}
                                            />
                                        )}
                                    </div>
                                </div>

                                <div className="w-20 text-left">
                                    <span className={`text-xs font-semibold ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
                                        {formatCurrency(sport.pl)}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {chartData.length > 1 && (
                    <div className="flex items-center mt-2 pl-10 pr-20">
                        <div className="w-1/2 flex justify-between text-[10px] text-slate-600 pr-1">
                            <span>{isUnits ? `-${maxAbsPL.toFixed(0)}U` : `-$${maxAbsPL >= 1000 ? `${(maxAbsPL / 1000).toFixed(0)}k` : maxAbsPL.toFixed(0)}`}</span>
                            <span>{isUnits ? `-${(maxAbsPL / 2).toFixed(0)}U` : `-$${maxAbsPL >= 1000 ? `${(maxAbsPL / 2000).toFixed(0)}k` : (maxAbsPL / 2).toFixed(0)}`}</span>
                        </div>
                        <div className="w-px" />
                        <div className="w-1/2 flex justify-between text-[10px] text-slate-600 pl-1">
                            <span>{isUnits ? `+${(maxAbsPL / 2).toFixed(0)}U` : `+$${maxAbsPL >= 1000 ? `${(maxAbsPL / 2000).toFixed(0)}k` : (maxAbsPL / 2).toFixed(0)}`}</span>
                            <span>{isUnits ? `+${maxAbsPL.toFixed(0)}U` : `+$${maxAbsPL >= 1000 ? `${(maxAbsPL / 1000).toFixed(0)}k` : maxAbsPL.toFixed(0)}`}</span>
                        </div>
                    </div>
                )}
            </div>

            {/* Divider */}
            <div className="border-t border-slate-700/50 my-4" />

            {/* Detailed List */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <div className="text-xs text-slate-500 uppercase tracking-wider">Details by Sport</div>
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500">Sort:</span>
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="px-2 py-1 bg-slate-800 border border-slate-700 rounded-lg text-xs text-white focus:outline-none focus:border-emerald-500"
                        >
                            <option value="pl">P/L</option>
                            <option value="roi">ROI</option>
                        </select>
                    </div>
                </div>

                <div className="space-y-2 max-h-[340px] overflow-y-auto pr-1">
                    {sportData.length === 0 ? (
                        <div className="text-center py-8 text-slate-500">
                            No sport data available
                        </div>
                    ) : (
                        sportData.map((sport) => (
                            <div
                                key={sport.name}
                                className="bg-slate-800/30 rounded-lg p-3 border border-slate-700/30 hover:border-slate-600/50 transition-all"
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2.5">
                                        <div
                                            className="w-7 h-7 rounded-md flex items-center justify-center text-[10px] font-bold text-white"
                                            style={{ backgroundColor: getSportColor(sport.name) }}
                                        >
                                            {sport.name.substring(0, 3).toUpperCase()}
                                        </div>
                                        <div className="text-sm text-white font-medium">{sport.name.toUpperCase()}</div>
                                    </div>
                                    <div className={`text-sm font-bold ${sport.pl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                        {formatCurrencyFull(sport.pl)}
                                    </div>
                                </div>

                                <div className="mt-2 pt-2 border-t border-slate-700/20 space-y-1.5">
                                    <div className="flex items-center justify-between text-xs">
                                        <div className="flex items-center gap-4">
                                            <span className="text-slate-400">
                                                <span className="text-slate-300">{sport.bets}</span> bets
                                            </span>
                                            <span className="text-slate-400">
                                                <span className="text-slate-300">{sport.wins}W</span> - <span className="text-slate-300">{sport.losses}L</span>
                                            </span>
                                            <span className="text-slate-400">
                                                Win Rate: <span className="text-slate-300">{sport.winRate.toFixed(1)}%</span>
                                            </span>
                                        </div>
                                        <div className="text-slate-500">
                                            ROI: <span className={`font-medium ${sport.roi >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                                {sport.roi >= 0 ? '+' : ''}{sport.roi.toFixed(1)}%
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between text-xs">
                                        <div className="flex items-center gap-4">
                                            <span className="text-slate-400">
                                                Staked: <span className="text-slate-300">{formatStaked(sport.totalStaked)}</span>
                                            </span>
                                            <span className="text-slate-400">
                                                Returned: <span className="text-slate-300">{formatStaked(sport.totalReturned)}</span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}

export default SportBreakdown;
